import React from "react";
import { getGuestPrepaymentPercent } from "../../../utils/prepayment";

const CANCELLATION_LABELS = {
  FREE_UPTO_28_DAYS: "Free up to 28 days before check-in date",
  FREE_UPTO_14_DAYS: "Free up to 14 days before check-in date",
  FREE_UPTO_7_DAYS: "Free up to 7 days before check-in date",
  FREE_UPTO_48_HOURS: "Free up to 48 hours before check-in date",
  NON_REFUNDABLE: "Non-refundable (selected dates)",
};

const formatCancellation = (value) => {
  if (!value) return "N/A";
  if (CANCELLATION_LABELS[value]) return CANCELLATION_LABELS[value];
  return String(value)
    .split("_")
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(" ");
};

const formatTime = (value) => {
  if (!value) return "N/A";
  const str = String(value);
  // Keep HH:MM if seconds present
  return str.length >= 5 ? str.slice(0, 5) : str;
};

const PolicyRow = ({ title, children }) => (
  <div className="border-b border-gray-100 py-3 last:border-b-0">
    <div className="text-sm font-semibold text-gray-500 mb-1">{title}</div>
    <div className="text-base text-gray-900">{children}</div>
  </div>
);

const PrepaymentPolicyModal = ({ isOpen, onClose, villaData }) => {
  if (!isOpen || !villaData) return null;

  const guestPrepaymentPercent = getGuestPrepaymentPercent(
    villaData.advance_percent,
    villaData.commmission
  );

  const policies = Array.isArray(villaData.policies)
    ? villaData.policies.filter(Boolean)
    : [];
  const houseRules = Array.isArray(villaData.houserules)
    ? villaData.houserules.filter(Boolean)
    : [];

  return (
    <div
      className="fixed inset-0 z-[200000] flex items-center justify-center bg-black/50 backdrop-blur-sm p-3"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-xl font-jt"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-gray-100 px-5 py-4 flex items-center justify-between rounded-t-2xl">
          <h2 className="text-xl font-bold text-gray-900">Policies</h2>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="text-gray-500 hover:text-black text-2xl leading-none px-1"
          >
            ×
          </button>
        </div>

        <div className="px-5 py-2">
          <p className="text-sm text-gray-500 mb-2">
            {villaData.listing_name || villaData.property_name}
          </p>

          <PolicyRow title="Pre-Payment">
            {guestPrepaymentPercent != null
              ? `${guestPrepaymentPercent}% of total booking amount charged in advance`
              : "N/A"}
          </PolicyRow>

          <PolicyRow title="Damage Deposit">
            {villaData.damage_deposit != null
              ? `₹${villaData.damage_deposit} refundable deposit at check-in`
              : "N/A"}
          </PolicyRow>

          <PolicyRow title="Cancellation">
            {formatCancellation(villaData.cancellation_policy)}
            {villaData.cancellation_policy === "NON_REFUNDABLE" &&
            Array.isArray(villaData.non_refundable_dates) &&
            villaData.non_refundable_dates.length > 0 ? (
              <ul className="list-disc pl-5 mt-2 space-y-1 text-sm font-normal">
                {villaData.non_refundable_dates.map((d) => (
                  <li key={d}>{d}</li>
                ))}
              </ul>
            ) : null}
          </PolicyRow>

          <PolicyRow title="Check-in">
            From {formatTime(villaData.check_in_start_time)}
            {villaData.check_in_end_time
              ? ` · Until ${formatTime(villaData.check_in_end_time)}`
              : ""}
          </PolicyRow>

          <PolicyRow title="Check-out">
            Before {formatTime(villaData.check_out_time)}
          </PolicyRow>

          {policies.length > 0 ? (
            <PolicyRow title="Fine Print">
              <ul className="list-disc pl-5 space-y-2 text-[15px] font-normal">
                {policies.map((item, i) => (
                  <li key={`policy-${i}`}>{item}</li>
                ))}
              </ul>
            </PolicyRow>
          ) : null}

          {houseRules.length > 0 ? (
            <PolicyRow title="House Rules">
              <ul className="list-disc pl-5 space-y-2 text-[15px] font-normal">
                {houseRules.map((item, i) => (
                  <li key={`rule-${i}`}>{item}</li>
                ))}
              </ul>
            </PolicyRow>
          ) : null}
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-5 py-3 rounded-b-2xl">
          <button
            type="button"
            onClick={onClose}
            className="w-full bg-black text-white font-semibold py-2.5 rounded-full"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrepaymentPolicyModal;
